import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { WhatsappLogo, Envelope, ArrowRight } from '@phosphor-icons/react';
import { CONTACT } from '@/lib/constants';

// Stable list of workflow attribute flags.
const WORKFLOW_FLAGS = [
  { key: 'is_repetitive', label: 'Is it repetitive?' },
  { key: 'is_rule_based', label: 'Is it rule-based?' },
  { key: 'is_digital_input', label: 'Is the input digital?' },
  { key: 'is_predictable_output', label: 'Is the output predictable?' },
  { key: 'is_high_volume', label: 'Is the volume high?' },
  { key: 'requires_judgment', label: 'Requires professional judgment?' },
];

const WorkflowCard = ({ workflow, index, onUpdate }) => (
  <div className="border border-slate-200 rounded-2xl p-5 sm:p-6 mb-4 bg-white shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-teal-50 border border-teal-100 text-teal-700 font-bold flex items-center justify-center">
        {index + 1}
      </div>
      <Label className="text-base font-semibold text-slate-900">Workflow {index + 1}</Label>
    </div>
    <Input
      value={workflow.name}
      onChange={(e) => onUpdate(index, 'name', e.target.value)}
      placeholder="e.g., Customer Complaint Handling, Monthly Reporting Process"
      className="mb-2"
      data-testid={`workflow-${index}-name-input`}
    />
    <p className="text-xs text-slate-500 mb-5 leading-relaxed">
      Please enter a meaningful workflow name, such as <em>Customer Complaint Handling</em>,{' '}
      <em>Monthly Reporting Process</em>, <em>Procurement Approval Workflow</em>,{' '}
      <em>Student Application Review</em>, or <em>PhD Progress Monitoring</em>.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {WORKFLOW_FLAGS.map(({ key, label }) => (
        <label
          key={key}
          htmlFor={`wf-${index}-${key}`}
          className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-teal-400 hover:bg-teal-50/50 cursor-pointer transition-colors"
        >
          <Checkbox
            id={`wf-${index}-${key}`}
            checked={workflow[key]}
            onCheckedChange={(checked) => onUpdate(index, key, checked)}
            data-testid={`workflow-${index}-${key}-checkbox`}
          />
          <span className="text-sm text-slate-700 select-none">{label}</span>
        </label>
      ))}
    </div>
  </div>
);

const ConsentBlock = ({ consent, onChange }) => (
  <div className="border border-amber-200 bg-amber-50/50 rounded-2xl p-5 mt-6">
    <div className="flex items-start gap-3">
      <Checkbox
        id="consent"
        checked={consent}
        onCheckedChange={onChange}
        data-testid="consent-checkbox"
        className="mt-0.5"
      />
      <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer text-slate-700">
        I agree that my responses may be used to generate a diagnostic report and that
        Aurea Digital Solutions may contact me regarding consultancy, training, or related services.
      </Label>
    </div>
  </div>
);

const ConsultationPreview = () => (
  <div className="mt-8 rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 text-white shadow-lg">
    <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-teal-300 mb-2">After You Submit</div>
    <h3 className="text-xl sm:text-2xl font-bold mb-2">Ready to Transform Your Workflow?</h3>
    <p className="text-slate-200 text-sm leading-relaxed mb-4">
      Once you submit, you will receive an executive diagnostic report with a SMART-Workflow
      roadmap tailored to your scores. Book a consultation with Aurea Digital Solutions
      to put it into action.
    </p>
    <div className="flex flex-col sm:flex-row gap-3">
      <a
        href={CONTACT.WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors"
        data-testid="assessment-cta-whatsapp"
      >
        <WhatsappLogo size={18} weight="fill" className="mr-2" />
        Book Consultation via WhatsApp
      </a>
      <a
        href={CONTACT.EMAIL_HREF}
        className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border-2 border-white/40 hover:bg-white/10 text-white text-sm font-semibold transition-colors"
        data-testid="assessment-cta-email"
      >
        <Envelope size={18} weight="bold" className="mr-2" />
        Email Aurea Digital Team
      </a>
    </div>
    <div className="mt-4 text-xs text-slate-400 flex items-center gap-2">
      <ArrowRight size={14} />
      <span>{CONTACT.COMPANY} · {CONTACT.WHATSAPP_NUMBER} · {CONTACT.EMAIL}</span>
    </div>
  </div>
);

const WorkflowsStep = ({ workflows, consent, onUpdateWorkflow, onConsentChange }) => (
  <div data-testid="workflows-consent-step">
    <div className="mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
        AI Opportunity Matrix &amp; Consent
      </h2>
      <p className="text-slate-500 mt-1 text-sm sm:text-base">
        List up to three workflows you would like assessed. We classify each one against
        five automation signals to identify your best AI opportunities.
      </p>
    </div>
    {workflows.map((wf, idx) => (
      <WorkflowCard key={wf.slotId} workflow={wf} index={idx} onUpdate={onUpdateWorkflow} />
    ))}
    <ConsentBlock consent={consent} onChange={onConsentChange} />
    <ConsultationPreview />
  </div>
);

export default WorkflowsStep;
